import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

interface ImportarUsuariosDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface UsuarioRow {
  nombre: string;
  email: string;
  password: string;
  rol: string;
}

interface ParsedRow {
  data: UsuarioRow;
  errors: string[];
  rowIndex: number;
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'done';

const TEMPLATE_COLUMNS = ['nombre', 'email', 'password', 'rol'];

const COLUMN_LABELS: Record<string, string> = {
  nombre: 'Nombre *',
  email: 'Email *',
  password: 'Contraseña *',
  rol: 'Rol *',
};

const VALID_ROLES = ['admin', 'tecnico', 'gestor'];

export const ImportarUsuariosDialog: React.FC<ImportarUsuariosDialogProps> = ({
  open, onOpenChange, onSuccess
}) => {
  const { perfil } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<ImportStep>('upload');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState({ created: 0, errors: 0 });

  const reset = useCallback(() => {
    setStep('upload');
    setParsedRows([]);
    setProgress(0);
    setImportResult({ created: 0, errors: 0 });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleOpenChange = (value: boolean) => {
    if (!value) reset();
    onOpenChange(value);
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      TEMPLATE_COLUMNS.map(c => COLUMN_LABELS[c] || c),
      ['Juan López', 'juan@empresa.com', 'password123', 'tecnico'],
      ['María García', 'maria@empresa.com', 'password456', 'admin'],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
    ws['!cols'] = TEMPLATE_COLUMNS.map(() => ({ wch: 22 }));
    XLSX.writeFile(wb, 'plantilla_usuarios.xlsx');
  };

  const parseFile = async (file: File) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' });

        if (jsonData.length === 0) {
          toast({ title: 'Archivo vacío', description: 'El archivo no contiene datos.', variant: 'destructive' });
          return;
        }

        const reverseMap: Record<string, string> = {};
        for (const key of TEMPLATE_COLUMNS) {
          reverseMap[(COLUMN_LABELS[key] || key).toLowerCase().replace(/\s*\*\s*/g, '').trim()] = key;
          reverseMap[key.toLowerCase()] = key;
        }

        const seenEmails = new Set<string>();
        const rows: ParsedRow[] = jsonData.map((row, idx) => {
          const mapped: Record<string, string> = {};
          for (const [header, value] of Object.entries(row)) {
            const normalizedHeader = header.toLowerCase().replace(/\s*\*\s*/g, '').trim();
            const matchedKey = reverseMap[normalizedHeader];
            if (matchedKey) mapped[matchedKey] = String(value || '').trim();
          }

          const errors: string[] = [];
          if (!mapped.nombre) errors.push('Falta nombre');
          if (!mapped.email) errors.push('Falta email');
          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mapped.email)) errors.push('Email inválido');
          else if (seenEmails.has(mapped.email.toLowerCase())) errors.push('Email duplicado');
          else seenEmails.add(mapped.email.toLowerCase());
          
          if (!mapped.password) errors.push('Falta contraseña');
          else if (mapped.password.length < 6) errors.push('Contraseña muy corta (min 6)');
          
          if (!mapped.rol) errors.push('Falta rol');
          else if (!VALID_ROLES.includes(mapped.rol.toLowerCase())) errors.push(`Rol inválido (usa: ${VALID_ROLES.join(', ')})`);

          return { data: mapped as unknown as UsuarioRow, errors, rowIndex: idx + 2 };
        });

        setParsedRows(rows);
        setStep('preview');
      } catch {
        toast({ title: 'Error al leer archivo', description: 'No se pudo parsear el archivo.', variant: 'destructive' });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  };

  const validRows = parsedRows.filter(r => r.errors.length === 0);
  const invalidRows = parsedRows.filter(r => r.errors.length > 0);

  const handleImport = async () => {
    if (!perfil?.empresa_id) return;
    setStep('importing');
    let created = 0;
    let errors = 0;
    const totalOps = validRows.length;

    // Create users one by one via edge function (each creates auth user + profile)
    for (const row of validRows) {
      try {
        const response = await fetch('https://ppmfxnwbanrqgsmydzjo.supabase.co/functions/v1/create-user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer qseguridad-create-user-4dj39s92kd'
          },
          body: JSON.stringify({
            email: row.data.email,
            password: row.data.password,
            nombre: row.data.nombre,
            rol: row.data.rol.toLowerCase(),
            empresa_id: perfil.empresa_id
          })
        });

        if (response.ok) {
          created++;
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('Error creating user:', row.data.email, errorData);
          errors++;
        }
      } catch (err) {
        console.error('Error creating user:', row.data.email, err);
        errors++;
      }
      setProgress(Math.round(((created + errors) / totalOps) * 100));
    }

    setImportResult({ created, errors: errors + invalidRows.length });
    setStep('done');
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Importar Usuarios
          </DialogTitle>
          <DialogDescription>
            Importa usuarios masivamente desde un archivo Excel o CSV. Se crearán cuentas con acceso a la plataforma.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-6 py-4">
            <div className="flex justify-center">
              <Button variant="outline" onClick={downloadTemplate} className="gap-2">
                <Download className="h-4 w-4" />Descargar plantilla Excel
              </Button>
            </div>
            <div className="bg-muted/50 border rounded-lg p-3 text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">⚠️ Importante:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Roles válidos: <code>admin</code>, <code>tecnico</code>, <code>gestor</code></li>
                <li>La contraseña debe tener al menos 6 caracteres</li>
                <li>Los emails deben ser únicos</li>
              </ul>
            </div>
            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="font-medium">Haz clic para seleccionar un archivo</p>
              <p className="text-sm text-muted-foreground mt-1">Formatos: .xlsx, .xls, .csv</p>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileChange} />
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4 flex-1 min-h-0">
            <div className="flex items-center gap-3 flex-wrap">
              {validRows.length > 0 && <Badge variant="default" className="gap-1"><CheckCircle className="h-3 w-3" />{validRows.length} válidos</Badge>}
              {invalidRows.length > 0 && <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />{invalidRows.length} con errores</Badge>}
              <span className="text-sm text-muted-foreground">Total: {parsedRows.length} filas</span>
            </div>
            <ScrollArea className="h-[300px] border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead className="w-28">Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedRows.map((row) => (
                    <TableRow key={row.rowIndex} className={row.errors.length > 0 ? 'bg-destructive/10' : ''}>
                      <TableCell className="text-muted-foreground text-xs">{row.rowIndex}</TableCell>
                      <TableCell className="font-medium text-sm">{row.data.nombre || '—'}</TableCell>
                      <TableCell className="text-sm">{row.data.email || '—'}</TableCell>
                      <TableCell className="text-sm">{row.data.rol || '—'}</TableCell>
                      <TableCell>
                        {row.errors.length > 0 ? (
                          <span className="text-destructive text-xs flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />{row.errors[0]}
                          </span>
                        ) : (
                          <Badge variant="outline" className="text-xs gap-1 border-green-500 text-green-600">
                            <CheckCircle className="h-3 w-3" />OK
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
            <div className="flex justify-between items-center pt-2">
              <Button variant="outline" onClick={reset}>Cancelar</Button>
              <Button onClick={handleImport} disabled={validRows.length === 0} className="gap-2">
                <Upload className="h-4 w-4" />Crear {validRows.length} usuarios
              </Button>
            </div>
          </div>
        )}

        {step === 'importing' && (
          <div className="py-8 space-y-4">
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" /><span className="font-medium">Creando usuarios...</span>
            </div>
            <Progress value={progress} className="h-2" />
            <p className="text-center text-sm text-muted-foreground">{progress}% — Los usuarios se crean de uno en uno para garantizar la seguridad</p>
          </div>
        )}

        {step === 'done' && (
          <div className="py-8 space-y-4 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
            <h3 className="text-lg font-semibold">Importación completada</h3>
            <div className="flex justify-center gap-4 flex-wrap">
              {importResult.created > 0 && <Badge variant="default" className="text-sm gap-1"><CheckCircle className="h-3 w-3" />{importResult.created} creados</Badge>}
              {importResult.errors > 0 && <Badge variant="destructive" className="text-sm gap-1"><XCircle className="h-3 w-3" />{importResult.errors} con errores</Badge>}
            </div>
            <Button onClick={() => { handleOpenChange(false); onSuccess(); }} className="mt-4">Cerrar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
